<template>
  <div class="details reservationoverview pb-5">
    <div class="details__title px-5"></div>
    <div class="container">
      <h1 class="title is-3">{{ $t("reservationOverview") }}</h1>
      <div class="columns is-multiline is-mobile m-0">
        <div class="column is-full p-0">
          <div
            class="details__content__box columns is-align-items-flex-end is-align-items-bottom m-0 mb-5"
          >
            <div class="column">
              <label for="startdateStart">{{
                $t("dateTimeSelection.startDateFrom")
              }}</label>
              <input
                id="startdateStart"
                type="date"
                :placeholder="$t('reservationoverview.startdate_start')"
                v-model="fromDate"
              />
            </div>
            <div class="column">
              <label class="" for="startdateEnd">
                {{ $t("dateTimeSelection.startDateTo") }}</label
              >
              <input
                id="startdateEnd"
                type="date"
                :placeholder="$t('reservationoverview.startdate_end')"
                v-model="toDate"
              />
            </div>

            <div class="column is-one-fifth">
              <button
                @click="findReservations"
                :disabled="buttonDisabled"
                class="c-button c-button--secondary"
              >
                {{ $t("apply") }}
              </button>
            </div>
          </div>
        </div>

        <div class="column is-full p-0">
          <div
            class="details__content__box columns is-align-items-flex-end is-align-items-bottom m-0 mb-5"
          >
            <div class="column">
              <input
                id="cityInput"
                type="text"
                :placeholder="$t('reservationoverview.omniSearch')"
                v-model="omniFilter"
              />
            </div>

            <div class="column">
              <b>{{ $t("show") }}:</b><br />
              <label class="pr-3">
                <input type="radio" v-model="visibleItems" value="FUTURE" />
                {{ $t("future") }}
              </label>
              <label class="pr-3">
                <input type="radio" v-model="visibleItems" value="CURRENT" />
                {{ $t("current") }}
              </label>
              <label>
                <input type="radio" v-model="visibleItems" value="ALL" />
                {{ $t("all") }}
              </label>
            </div>

            <div class="column">
              <div class="buttons has-addons is-right">
                <button
                  @click="toggleFilterable(filterable)"
                  class="c-button c-button--ghost"
                  :class="{
                    'c-button--primary': filterable.isSelected
                  }"
                  v-for="filterable in filterables"
                  v-bind:key="filterable.id"
                >
                  <i
                    v-if="filterable.isSelected"
                    class="fa-regular fa-square-check pr-3"
                  ></i>
                  <i v-else class="fa-regular fa-square pr-3"></i>
                  {{ filterable.title }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="column is-full p-0">
          <div class="details__content__box columns m-0 mb-5">
            <div class="column">
              <h5
                class="title is-5 width-100"
                :class="[
                  isCollapsed ? 'mb-0' : '',
                  collapsible ? 'title--collapsible-title' : ''
                ]"
                @click="collapsible ? (isCollapsed = !isCollapsed) : null"
              >
                <i
                  v-if="collapsible"
                  class="fa collapse-icon"
                  :class="isCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'"
                ></i>
                <span v-html="$t('reservationOverviewMessage.header')"></span>
              </h5>

              <div v-show="!collapsible || !isCollapsed">
                <span v-html="$t('reservationOverviewMessage.body')"></span>
                <a href="mailto:zakelijk@lookingforbooking.com"
                  >zakelijk@lookingforbooking.com</a
                >
              </div>
            </div>
          </div>
        </div>

        <div class="column is-full p-0" v-if="hotelReservations.length == 500">
          <div class="details__content__box columns has-text-centered">
            <div class="column content">
              <p class="u-text-color-orange">
                <i
                  class="fa-solid fa-triangle-exclamation u-text-color-orange"
                ></i>
                {{ $t("reservationOverviewLimitedResults") }}
              </p>
            </div>
          </div>
        </div>

        <!-- Tabs Navigation -->
        <div class="column is-full p-0">
          <div class="tabs is-boxed">
            <ul>
              <li :class="{ 'is-active': activeTab === 'hotels' }">
                <a @click="activeTab = 'hotels'">
                  <span class="icon is-small"
                    ><i class="fas fa-hotel"></i
                  ></span>
                  <span>{{ $t("nav.hotels") }}</span>
                  <span class="tag ml-2">
                    {{
                      (visibleReservations || []).filter((r) => {
                        const d = r.guestsWithDates?.[0]?.dates?.[0]?.date;
                        return (
                          d &&
                          (visibleItems === "ALL" ||
                            (visibleItems === "FUTURE" &&
                              moment(d).isAfter()) ||
                            (visibleItems === "CURRENT" &&
                              moment(d).isSameOrBefore() &&
                              moment(r.endDate).isSameOrAfter()))
                        );
                      }).length
                    }}
                  </span>
                </a>
              </li>
              <li :class="{ 'is-active': activeTab === 'meetings' }">
                <a @click="activeTab = 'meetings'">
                  <span class="icon is-small"
                    ><i class="fas fa-users"></i
                  ></span>
                  <span>{{ $t("nav.meetingReservation") }}</span>
                  <span class="tag ml-2">
                    {{
                      visibleMeetingReservations.filter(
                        (r) =>
                          visibleItems === "ALL" ||
                          (visibleItems === "FUTURE" &&
                            moment(r.startDate).isAfter()) ||
                          (visibleItems === "CURRENT" &&
                            moment(r.startDate).isSameOrBefore() &&
                            moment(r.endDate).isSameOrAfter())
                      ).length
                    }}
                  </span>
                </a>
              </li>
              <li :class="{ 'is-active': activeTab === 'transport' }">
                <a @click="activeTab = 'transport'">
                  <span class="icon is-small"
                    ><i class="fas fa-plane"></i
                  ></span>
                  <span>{{ $t("nav.flightForm") }}</span>
                  <span class="tag ml-2">
                    {{
                      (visibleTransportReservations || []).filter((r) => {
                        const flightDate = r.flights?.[0]?.departureDate;
                        return (
                          flightDate &&
                          (visibleItems === "ALL" ||
                            (visibleItems === "FUTURE" &&
                              moment(flightDate).isAfter()) ||
                            (visibleItems === "CURRENT" &&
                              moment(flightDate).isSameOrBefore() &&
                              moment(flightDate).isSameOrAfter()))
                        );
                      }).length
                    }}
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <!-- Loading State -->
        <div
          class="details__content container loading-container mt-5"
          v-if="isLoading || isFiltering"
        >
          <div class="details__content__box has-text-centered">
            <h3 class="title is-3">{{ $t("reservationoverview.loading") }}</h3>
            <br />
            <img width="50" height="60" src="@/assets/images/loader.svg" />
          </div>
        </div>

        <!-- Tab Content -->
        <div class="column is-full p-0" v-if="!isLoading && !isFiltering">
          <!-- Hotels Tab -->
          <div v-show="activeTab === 'hotels'" class="tab-content">
            <HotelReservationItem
              v-for="r in visibleReservations"
              v-bind:key="r.id"
              :reservation="r"
              @rebook="rebook"
              @cancelledItems="cancelledItems"
              :visibleItems="visibleItems"
            />
          </div>

          <!-- Meetings Tab -->
          <div v-show="activeTab === 'meetings'" class="tab-content">
            <MeetingReservationItem
              v-for="r in visibleMeetingReservations"
              v-bind:key="r.id"
              :reservation="r"
              :visibleItems="visibleItems"
            />
          </div>

          <!-- Transport Tab -->
          <div v-show="activeTab === 'transport'" class="tab-content">
            <FlightReservationItem
              v-for="r in visibleTransportReservations"
              v-bind:key="r.id"
              :reservation="r"
              :visibleItems="visibleItems"
            />
          </div>
        </div>
      </div>
    </div>
    <NotificationMessage
      :level="notification.level"
      :autohide="false"
      :title="notification.title"
      :message="notification.message"
      :show="notification.show"
      @toggle="toggleNotification"
    />
  </div>
</template>

<script>
import HotelReservationItem from "@/components/reservationoverview/HotelReservationItem.vue";
import FlightReservationItem from "@/components/reservationoverview/FlightReservationItem.vue";
import MeetingReservationItem from "@/components/reservationoverview/MeetingReservationItem.vue";
import ReservationHelper from "@/helpers/reservation.helper";
import moment from "moment";

export default {
  data() {
    return {
      filterables: [],
      hotelReservations: [],
      meetingReservations: [],
      transportReservations: [],
      fromDate: new moment().subtract(1, "weeks").format("yyyy-MM-DD"),
      toDate: new moment().add(4, "weeks").format("YYYY-MM-DD"),
      cityInput: "",
      hotelName: "",
      omniFilter: "",
      appliedOmniFilter: "",
      isFiltering: false,
      filterTimeout: null,
      buttonDisabled: false,
      visibleItems: "FUTURE",
      isLoading: true,
      activeTab: "hotels",
      pagination: {
        currentPage: 1,
        pageSize: 2
      },
      isCollapsed: true,
      collapsible: {
        type: Boolean,
        default: true
      }
    };
  },
  components: {
    HotelReservationItem,
    FlightReservationItem,
    MeetingReservationItem
  },
  async created() {
    this.findReservations();
    this.addFilterable("cancelled", this.$t("hideCancelled"), true);
  },

  methods: {
    moment,
    cancelledItems() {
      this.notification.message = this.$t("confirmCancellationRequested");
      this.notification.level = "info";
      this.notification.show = true;

      setTimeout(() => {
        this.notification.show = false;
      }, 10 * 1000);
      this.findReservations();
    },
    async findReservations() {
      if (this.fromDate && this.toDate) {
        this.isLoading = true;
        this.hotelReservations = [];
        this.meetingReservations = [];
        this.transportReservations = [];
        this.buttonDisabled = true;

        let companyInfo = JSON.parse(localStorage.companyInfo);

        let companyId = companyInfo?.company?.id ?? "unknown";

        let reservations =
          (await ReservationHelper.getReservations(
            this.fromDate,
            this.toDate
          )) || {};

        this.hotelReservations = reservations?.hotelReservations ?? [];
        this.meetingReservations = reservations?.meetingReservations ?? [];
        this.transportReservations = reservations?.transportReservations ?? [];

        this.buttonDisabled = false;
        if (reservations.hotelReservations) {
          if (reservations.hotelReservations.length == 500) {
            let message = `dateRangeTooBig 500 returned: Selection ${this.fromDate} - ${this.toDate}`;
            this.track("generalError", {
              page: "reservationOverview",
              message,
              companyId
            });
          }
        } else {
          this.notification.title = this.$t("genericErrorTitle");
          this.notification.message = this.$t("dateRangeTooBig");
          let message = `dateRangeTooBig: Selection ${this.fromDate} - ${this.toDate}`;
          this.track("generalError", {
            page: "reservationOverview",
            message,
            companyId
          });

          this.notification.level = "danger";
          this.notification.show = true;
          setTimeout(() => {
            this.notification.show = false;
          }, 10 * 1000);
        }
        this.isLoading = false;
      } else {
        this.notification.title = this.$t("genericErrorTitle");
        this.notification.message = this.$t("enterDates");
        this.notification.level = "warning";
        this.notification.show = true;
        setTimeout(() => {
          this.notification.show = false;
        }, 10 * 1000);
      }
    },

    rebook(data) {
      const { reservation, dateRange, adults, employees, rebookOptions } = data;

      let params = {
        hotelId: reservation.hotel.id
      };
      params.rooms = employees.split(",").length ?? 1;
      params.guests = "";
      params.adults = 1;
      params.preselectedBusinessUnitId = "";
      if (rebookOptions.sameGuest) {
        params.guests = employees;
        params.adults = adults;
      }
      if (rebookOptions.sameBusinessUnit) {
        params.preselectedBusinessUnitId = reservation.businessUnit.id;
      }
      params.locationPath = "direct";
      if (rebookOptions.sameDate) {
        params.checkin = dateRange.startDate.format("YYYY-MM-DD");
        params.nights = dateRange.endDate.diff(dateRange.startDate, "days") + 1;
        params.locationPath = "hasChosenDate";
      }

      this.$router.push({
        name: this.PAGES.HotelRebook,
        params
      });
    },
    addBooking(hotelName, startDate, endDate, guests = []) {},
    addFilterable(id, title, isSelected = false) {
      this.filterables.push({ id, title, isSelected });
    },
    toggleFilterable(filterable) {
      filterable.isSelected = !filterable.isSelected;
    },
    getReservationComponent(type) {
      const components = {
        Hotel: "HotelReservationItem",
        Vlucht: "FlightReservationItem",
        Vergaderarrangement: "MeetingReservationItem"
      };
      return components[type] || "HotelReservationItem"; // Default to Hotel if type is unknown
    }
  },
  watch: {
    omniFilter(newVal) {
      if (this.filterTimeout) clearTimeout(this.filterTimeout);
      this.isFiltering = true;
      this.filterTimeout = setTimeout(() => {
        this.appliedOmniFilter = newVal;
        this.isFiltering = false;
      }, 500);
    }
  },
  computed: {
    visibleMeetingReservations() {
      return this.meetingReservations.filter((reservation) => {
        return (
          this.appliedOmniFilter === "" ||
          reservation.meetingTitle
            ?.toLowerCase()
            .includes(this.appliedOmniFilter.toLowerCase())
        );
      });
    },

    visibleTransportReservations() {
      return this.transportReservations.filter((reservation) => {
        return (
          this.appliedOmniFilter === "" ||
          reservation.transportType
            ?.toLowerCase()
            .includes(this.appliedOmniFilter.toLowerCase())
        );
      });
    },

    visibleReservations() {
      const omniFilter = this.appliedOmniFilter.toLowerCase();
      const hideCancelled = this.filterables[0].isSelected;

      return this.hotelReservations.filter((reservation) => {
        const hotelName = reservation.hotel?.name?.toLowerCase();
        const hotelCity = reservation.hotel?.city?.toLowerCase();
        const employees = reservation.guestsWithDates
          .map((gwd) => {
            return (
              gwd.employee.firstName +
              " " +
              gwd.employee.lastName
            ).toLowerCase();
          })
          .join(" ");

        let matchesOmni =
          omniFilter === ""
            ? true
            : hotelName.includes(omniFilter) ||
              hotelCity.includes(omniFilter) ||
              employees.includes(omniFilter);
        let matchesFilterables = true;
        if (hideCancelled && reservation.allDatesCancelled) {
          matchesFilterables = false;
        }

        return matchesFilterables && matchesOmni;
      });
    }
  }
};
</script>
<style scoped>
.details__title {
  background: url("@/assets/images/reserveringsoverzicht.jpg") center center
    no-repeat;
  background-size: cover;
}

.tabs {
  margin-bottom: 0 !important;
}

.tabs.is-boxed li.is-active a {
  background-color: #fff;
  border-color: #dbdbdb;
  border-bottom-color: transparent !important;
}

.tab-content {
  background-color: white;
  padding: 1rem;
  border: 1px solid #dbdbdb;
  border-top: none;
  border-radius: 0 0 4px 4px;
}

.tabs .tag {
  background-color: #f5f5f5;
  color: #4a4a4a;
}

.title.width-100 {
  width: 100%;
}

.collapsible-title {
  cursor: pointer;
  user-select: none;
}
</style>
